import React, { useState, useContext, useEffect } from 'react';
import {
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  Box,
  Collapse,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  Drawer,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinkIcon from '@mui/icons-material/Link';
import CreateIcon from '@mui/icons-material/Create';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { ThemeContext } from '../../../lib/contexts/ThemeContext';
import AddressEditCard from './AddressEditCard';
import { OrdersContext } from '../../../lib/contexts/OrdersContext';
import { getUserAddress } from '../../../lib/api/getUserAddress';
import ErrorIcon from '@mui/icons-material/Error';

// Utility function to format the date
const formatDate = (isoString) => {
  const date = new Date(isoString);
  const options = { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' };
  return date.toLocaleDateString('en-US', options);
};

// Utility function to format the address
const formatAddress = (location) => {
  const { address, address_2, city, state, zip, country } = location;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <Typography
        variant="body1"
        sx={{ fontFamily: 'Montserrat' }}
      >
        {address}
        {address_2 ? `, ${address_2}` : ''}
      </Typography>
      <Typography
        variant="body1"
        sx={{ fontFamily: 'Montserrat' }}
      >
        {city} {state}
      </Typography>
      <Typography
        variant="body1"
        sx={{ fontFamily: 'Montserrat' }}
      >
        {country} {zip}
      </Typography>
    </Box>
  );
};

const DeliveryDetails = ({ delivery }) => {
  const [expanded, setExpanded] = useState(false);
  const {
    state: { darkMode, ordersData },
    actions: { adjustTag, updateOrderAddress },
  } = useContext(OrdersContext);
  const [deliveryStatus, setDeliveryStatus] = useState(adjustTag(delivery));
  const [trackingDetails, setTrackingDetails] = useState({
    preparing: false,
    confirmed: false,
    pickedUp: false,
    delivered: false,
  });
  const [openEditDrawer, setOpenEditDrawer] = useState(false);
  const [userDefaultAddress, setUserDefaultAddress] = useState(null);
  // console.log('here is delivery: ', delivery);

  useEffect(() => {
    // Flag to prevent state updates after component unmounts
    let isMounted = true;

    const fetchUserAddress = async () => {
      const address = await getUserAddress();
      // Only update state if component is still mounted
      if (isMounted && address?.locations) {
        for (let loc of address.locations) {
          if (loc.is_default === 1) {
            setUserDefaultAddress(loc);
            if (delivery.location_id !== loc.id) {
              updateOrderAddress(delivery.id, loc.id);
            }
          }
        }
      }
    };

    if (delivery.location_id === null) {
      fetchUserAddress();
    }

    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [delivery.location_id, delivery.id, updateOrderAddress]);

  useEffect(() => {
    setTrackingDetails({
      preparing:
        deliveryStatus === 'PREPARING' ||
        deliveryStatus === 'CONFIRMED' ||
        deliveryStatus === 'PICKED UP' ||
        deliveryStatus === 'DELIVERED',
      confirmed: deliveryStatus === 'CONFIRMED' || deliveryStatus === 'PICKED UP' || deliveryStatus === 'DELIVERED',
      pickedUp: deliveryStatus === 'PICKED UP' || deliveryStatus === 'DELIVERED',
      delivered: deliveryStatus === 'DELIVERED',
    });
  }, [deliveryStatus]);

  useEffect(() => {
    const updatedOrder = ordersData.find((order) => order.id === delivery.id);
    if (updatedOrder) {
      setDeliveryStatus(adjustTag(updatedOrder));
    }
  }, [ordersData, delivery.id, adjustTag]);

  const getCheckBox = (stage) => {
    // console.log('trackingDetails.stage', trackingDetails.stage)
    return (
      <Box
        id="checkbox-container"
        sx={{
          width: '20px',
          height: '20px',
        }}
      >
        <Box
          id="checkbox"
          sx={{
            width: '20px',
            height: '20px',
            flexShrink: 0,
            borderRadius: '4px',
            border: !trackingDetails[stage] ? '1px solid var(--color11, #58489A)' : 'none',
            background: trackingDetails[stage] ? 'var(--Tangelo-Green, #00AD8C)' : '#FDFDFD',
            paddingLeft: '2px',
          }}
        >
          {trackingDetails[stage] && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              style={{
                flexShrink: 0,
              }}
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M13.8047 3.52876C14.0651 3.78911 14.0651 4.21122 13.8047 4.47157L6.4714 11.8049C6.21106 12.0653 5.78894 12.0653 5.5286 11.8049L2.19526 8.47157C1.93491 8.21122 1.93491 7.78911 2.19526 7.52876C2.45561 7.26841 2.87772 7.26841 3.13807 7.52876L6 10.3907L12.8619 3.52876C13.1223 3.26841 13.5444 3.26841 13.8047 3.52876Z"
                fill="#FDFDFD"
              />
            </svg>
          )}
        </Box>
      </Box>
    );
  };

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  // console.log('check out this awesome delivery status', deliveryStatus)

  if (!delivery) {
    return <Typography variant="body1">Delivery details not available</Typography>;
  }

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  // Generate tracking steps based on delivery status
  const generateTrackingSteps = (status) => {
    const allSteps = [
      { status: 'Preparing', date: delivery.preparing || '' },
      { status: 'Confirmed', date: delivery.confirmed || '' },
      { status: 'Picked Up', date: delivery.pickedUp || '' },
      { status: 'Delivered', date: delivery.delivered || '' },
    ];

    // const allSteps = [
    //     { status: 'Order Placed', date: delivery.orderPlaced || '' },
    //     { status: 'Shipped', date: delivery.shipped || '' },
    //     { status: 'Out for Delivery', date: '' },
    //     { status: 'Delivered', date: '' }
    // ];

    // Determine which steps are completed based on current status
    switch (status) {
      case 'Delivered':
        return allSteps.map((step) => ({ ...step, completed: true }));
      case 'Out for Delivery':
        return allSteps.map((step) => ({
          ...step,
          completed: adjustTag(step) !== 'Delivered',
        }));
      case 'Shipped':
        return allSteps.map((step) => ({
          ...step,
          completed: ['Order Placed', 'Shipped'].includes(adjustTag(step)),
        }));
      case 'Order Placed':
        return allSteps.map((step) => ({
          ...step,
          completed: adjustTag(step) === 'Order Placed',
        }));
      default:
        return allSteps.map((step) => ({ ...step, completed: false }));
    }
  };

  const trackingSteps = generateTrackingSteps(adjustTag(delivery));

  const handleEditButtonClick = () => {
    setOpenEditDrawer(true);
  };

  const handleCloseDrawer = () => {
    setOpenEditDrawer(false);
  };

  const handleTrackingLinkClick = () => {
    if (delivery.tracking_url) {
      window.open(delivery.tracking_url, '_blank');
    } else {
      console.warn('Tracking URL is not available');
    }
  };

  const cutoffTime = new Date(delivery.cutoff_time);
  const currentTime = new Date();

  return (
    <Box
      id="delivery-details-container"
      sx={{
        display: 'flex',
        width: '327px',
        padding: '24px',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '32px',
        borderRadius: '8px',
        border: '2px solid var(--color3, #F5F4F9)',
        background: 'var(--Gray-gray2, #F8F8F8)',
      }}
    >
      <Box
        id="delivery-details-body"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '24px',
          alignSelf: 'stretch',
        }}
      >
        <Box
          id="header"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            alignSelf: 'stretch',
          }}
        >
          <Typography
            sx={{
              color: 'var(--Gray-gray12, #171717)',
              fontFamily: 'Montserrat',
              fontSize: '20px',
              fontStyle: 'normal',
              fontWeight: 700,
              lineHeight: '125%', // 25px
            }}
          >
            Delivery Details
          </Typography>
        </Box>
        <Box
          id="body"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '24px',
            alignSelf: 'stretch',
          }}
        >
          {(deliveryStatus === 'FUTURE' ||
            deliveryStatus === 'PREPARING' ||
            deliveryStatus === 'CONFIRMED' ||
            deliveryStatus === 'PICKED UP' ||
            deliveryStatus === 'CANCELED') &&
            cutoffTime > currentTime && (
              <Box
                id="edit-window-container"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  alignSelf: 'stretch',
                }}
              >
                <Box
                  id="edit-window"
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: 'var(--3, 13px)',
                  }}
                >
                  <Typography
                    sx={{
                      color: 'var(--Gray-gray12, #171717)',
                      leadingTrim: 'both',
                      textEdge: 'cap',
                      fontFamily: 'Montserrat',
                      fontSize: '16px',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      lineHeight: 'normal',
                      opacity: deliveryStatus === 'CANCELED' ? 0.5 : 1,
                    }}
                  >
                    Menu Edit window:
                  </Typography>
                  <Typography
                    sx={{
                      color:
                        deliveryStatus === 'FUTURE' || deliveryStatus === 'PREPARING'
                          ? 'var(--secondary-mint-accessilbe, #1D7E6B)'
                          : '#6F6F6F',
                      leadingTrim: 'both',
                      textEdge: 'cap',
                      fontFamily: 'Montserrat',
                      fontSize: '18px',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      lineHeight: 'normal',
                      opacity: deliveryStatus === 'CANCELED' ? 0.5 : 1,
                    }}
                  >
                    {deliveryStatus === 'FUTURE' || deliveryStatus === 'PREPARING'
                      ? `${Math.ceil((cutoffTime - currentTime) / (1000 * 60 * 60 * 24))} days left`
                      : 'Closed'}
                  </Typography>
                </Box>
              </Box>
            )}
          <Box
            id="expected-delivery-container"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '16px',
            }}
          >
            <Typography
              sx={{
                color: 'var(--Gray-gray12, #171717)',
                leadingTrim: 'both',
                textEdge: 'cap',
                fontFamily: 'Montserrat',
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: 'normal',
              }}
            >
              {delivery.dropoff_time != null
                ? deliveryStatus === 'DELIVERED'
                  ? 'Delivered at:'
                  : 'Expected delivery:'
                : 'Placed At:'}
            </Typography>
            <Typography
              sx={{
                color: 'var(--Gray-gray12, #171717)',
                leadingTrim: 'both',
                textEdge: 'cap',
                fontFamily: 'Montserrat',
                fontSize: '18px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: 'normal',
              }}
            >
              {delivery.dropoff_time != null
                ? formatDate(delivery.dropoff_time)
                : delivery.placed_at
                ? formatDate(delivery.placed_at)
                : 'Pending'}
            </Typography>
          </Box>
          <Box
            id="address-container"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '16px',
              alignSelf: 'stretch',
            }}
          >
            <Typography
              sx={{
                color: 'var(--Gray-gray12, #171717)',
                leadingTrim: 'both',
                textEdge: 'cap',
                fontFamily: 'Montserrat',
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: 'normal',
              }}
            >
              Shipping address:
            </Typography>
            <Box
              sx={{
                color: 'var(--Gray-gray12, #171717)',
                fontFamily: 'Montserrat',
                fontSize: '18px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: 'normal',
                alignSelf: 'stretch',
              }}
            >
              {delivery?.location ? (
                formatAddress(delivery.location)
              ) : userDefaultAddress ? (
                formatAddress(userDefaultAddress)
              ) : (
                <Typography>No Address Information</Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {(deliveryStatus === 'CONFIRMED' || deliveryStatus === 'PICKED UP' || deliveryStatus === 'DELIVERED') && (
        <Accordion
          expanded={expanded === 'panel1'}
          onChange={handleChange('panel1')}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 'var(--0, 0px)',
            alignSelf: 'stretch',
            paddingRight: '18px',
            paddingLeft: '18px',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1-content"
            id="panel1-header"
            sx={{
              display: 'flex',
              // padding: 'var(--4, 18px)',
              padding: '4px',
              justifyContent: 'space-between',
              alignItems: 'center',
              alignSelf: 'stretch',
              // border: '1px solid var(--color6, #D6D2E7)',
              background: 'var(--color1, #FDFDFD)',
            }}
          >
            <Typography
              sx={{
                color: 'var(--Gray-gray12, #171717)',
                leadingTrim: 'both',
                textEdge: 'cap',
                fontFamily: 'Montserrat',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: 'normal',
              }}
            >
              Tracking Details
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box
              id="tracking-details-container"
              sx={{
                display: 'flex',
                padding: 'var(--4, 18px)',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'flex-start',
                gap: 'var(--4, 18px)',
                alignSelf: 'stretch',
                background: '#FFF',
              }}
            >
              <Box
                id="tracking-details-content"
                sx={{
                  display: 'flex',
                  padding: '0px 12px',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '2px',
                  alignSelf: 'stretch',
                }}
              >
                <Box
                  id="order-placed-label"
                  sx={{
                    display: 'flex',
                    height: '40px',
                    alignItems: 'flex-start',
                    gap: '12px',
                  }}
                >
                  <Box
                    id="order-placed-status"
                    sx={{
                      display: 'flex',
                      padding: '2px 0px',
                      alignItems: 'flex-start',
                    }}
                  >
                    {getCheckBox('preparing')}
                  </Box>
                  <Box
                    id="order-placed-details-container"
                    sx={{
                      display: 'flex',
                      paddingTop: '2px',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <Box
                      id="order-placed-details"
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: '4px',
                      }}
                    >
                      <Typography
                        sx={{
                          color: 'var(--Gray-gray12, #171717)',
                          fontFamily: 'Montserrat',
                          fontSize: '16px',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          lineHeight: '20px',
                        }}
                      >
                        Preparing
                      </Typography>
                      <Typography
                        id="date"
                        sx={{
                          color: 'var(--color11, #58489A)',
                          fontFamily: 'Montserrat',
                          fontSize: '12px',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          lineHeight: '14px',
                        }}
                      >
                        placeholder date
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box
                  id="shipped-line"
                  sx={{
                    width: '2px',
                    height: '24px',
                    left: '21px',
                    top: '18px',
                    marginBottom: '10px',
                    borderLeft: trackingDetails.shipped
                      ? '2px solid var(--Tangelo-Matcha-matcha8, #3DC1A8)'
                      : '2px dotted var(--color11, #58489A)',
                  }}
                />
                <Box
                  id="shipped-label"
                  sx={{
                    display: 'flex',
                    height: '40px',
                    alignItems: 'flex-start',
                    gap: '12px',
                  }}
                >
                  <Box
                    id="shipped-status"
                    sx={{
                      display: 'flex',
                      padding: '2px 0px',
                      alignItems: 'flex-start',
                    }}
                  >
                    {getCheckBox('confirmed')}
                  </Box>
                  <Box
                    id="shipped-details-container"
                    sx={{
                      display: 'flex',
                      paddingTop: '2px',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <Box
                      id="shipped-details"
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: '4px',
                      }}
                    >
                      <Typography
                        sx={{
                          color: 'var(--Gray-gray12, #171717)',
                          fontFamily: 'Montserrat',
                          fontSize: '16px',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          lineHeight: '20px',
                        }}
                      >
                        Confirmed
                      </Typography>
                      <Typography
                        id="date"
                        sx={{
                          color: 'var(--color11, #58489A)',
                          fontFamily: 'Montserrat',
                          fontSize: '12px',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          lineHeight: '14px',
                        }}
                      >
                        placeholder date
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box
                  id="out-for-delivery-line"
                  sx={{
                    width: '2px',
                    height: '24px',
                    left: '21px',
                    top: '18px',
                    marginBottom: '10px',
                    borderLeft: trackingDetails.outForDelivery
                      ? '2px solid var(--Tangelo-Matcha-matcha8, #3DC1A8)'
                      : '2px dotted var(--color11, #58489A)',
                  }}
                />
                <Box
                  id="out-for-delivery-label"
                  sx={{
                    display: 'flex',
                    height: '40px',
                    alignItems: 'flex-start',
                    gap: '12px',
                  }}
                >
                  <Box
                    id="out-for-delivery-status"
                    sx={{
                      display: 'flex',
                      padding: '2px 0px',
                      alignItems: 'flex-start',
                    }}
                  >
                    {getCheckBox('pickedUp')}
                  </Box>
                  <Box
                    id="out-for-delivery-details-container"
                    sx={{
                      display: 'flex',
                      paddingTop: '2px',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <Box
                      id="out-for-delivery-details"
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: '4px',
                      }}
                    >
                      <Typography
                        sx={{
                          color: 'var(--Gray-gray12, #171717)',
                          fontFamily: 'Montserrat',
                          fontSize: '16px',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          lineHeight: '20px',
                        }}
                      >
                        Picked Up
                      </Typography>
                      <Typography
                        id="date"
                        sx={{
                          color: 'var(--color11, #58489A)',
                          fontFamily: 'Montserrat',
                          fontSize: '12px',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          lineHeight: '14px',
                        }}
                      >
                        placeholder date
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box
                  id="delivered-line"
                  sx={{
                    width: '2px',
                    height: '24px',
                    left: '21px',
                    top: '18px',
                    marginBottom: '10px',
                    borderLeft: trackingDetails.delivered
                      ? '2px solid var(--Tangelo-Matcha-matcha8, #3DC1A8)'
                      : '2px dotted var(--color11, #58489A)',
                  }}
                />
                <Box
                  id="delivered-label"
                  sx={{
                    display: 'flex',
                    height: '40px',
                    alignItems: 'flex-start',
                    gap: '12px',
                  }}
                >
                  <Box
                    id="delivered-status"
                    sx={{
                      display: 'flex',
                      padding: '2px 0px',
                      alignItems: 'flex-start',
                    }}
                  >
                    {getCheckBox('delivered')}
                  </Box>
                  <Box
                    id="delivered-details-container"
                    sx={{
                      display: 'flex',
                      paddingTop: '2px',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <Box
                      id="delivered-details"
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: '4px',
                      }}
                    >
                      <Typography
                        sx={{
                          color: 'var(--Gray-gray12, #171717)',
                          fontFamily: 'Montserrat',
                          fontSize: '16px',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          lineHeight: '20px',
                        }}
                      >
                        Delivered
                      </Typography>
                      <Typography
                        id="date"
                        sx={{
                          color: 'var(--color11, #58489A)',
                          fontFamily: 'Montserrat',
                          fontSize: '12px',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          lineHeight: '14px',
                        }}
                      >
                        placeholder date
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Button
                id="tracking-details-button"
                sx={{
                  display: 'flex',
                  // width: '245px',
                  width: '200px',
                  height: 'var(--3, 36px)',
                  padding: 'var(--0, 0px) var(--3, 13px)',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 'var(--3, 13px)',
                  borderRadius: 'var(--3, 7px)',
                  border: 'var(--1, 2px) solid var(--color12, #1C124D)',
                  background: 'var(--color1, #FDFDFD)',
                }}
                onClick={handleTrackingLinkClick}
              >
                <Typography
                  sx={{
                    color: 'var(--color12, #1C124D)',
                    textAlign: 'center',
                    leadingTrim: 'both',
                    textEdge: 'cap',
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: 'normal',
                  }}
                >
                  Tracking Link
                </Typography>
                <LinkIcon
                  sx={{
                    width: 'var(--1, 20px)',
                    height: 'var(--1, 20px)',
                    flexShrink: 0,
                  }}
                />
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {deliveryStatus === 'FUTURE' && (
        <Button
          onClick={handleEditButtonClick}
          sx={{
            display: 'flex',
            width: '245px',
            height: 'var(--3, 36px)',
            padding: 'var(--0, 0px) var(--3, 13px)',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 'var(--3, 13px)',
            borderRadius: 'var(--3, 7px)',
            border: 'var(--1, 2px) solid var(--color12, #1C124D)',
            background: 'var(--color1, #FDFDFD)',
          }}
        >
          <CreateIcon
            sx={{
              width: 'var(--1, 20px)',
              height: 'var(--1, 20px)',
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              color: 'var(--color12, #1C124D)',
              textAlign: 'center',
              leadingTrim: 'both',
              textEdge: 'cap',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 700,
              lineHeight: 'normal',
            }}
          >
            Edit Delivery Details
          </Typography>
        </Button>
      )}
      {/* saving for later */}
      {false && (
        <Box
          id="alert-container"
          sx={{
            display: 'flex',
            padding: '16px',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '10px',
            alignSelf: 'stretch',
            borderRadius: '12px',
            border: '1px solid var(--Yellow-yellow11, #946800)',
            background: 'var(--Yellow-yellow3, #FFFBD1)',
          }}
        >
          <Typography
            sx={{
              flex: '1 0 0',
              color: 'var(--Gray-gray12, #171717)',
              leadingTrim: 'both',
              textEdge: 'cap',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: 'normal',
            }}
          >
            You cannot edit this address yet. Once we start{' '}
            <Typography
              component="span"
              sx={{
                color: 'var(--Gray-gray12, #171717)',
                leadingTrim: 'both',
                textEdge: 'cap',
                fontFamily: 'Montserrat',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: 'normal',
              }}
            >
              Preparing
            </Typography>{' '}
            your order, you may edit this address.
          </Typography>
        </Box>
      )}
      {(deliveryStatus === 'CONFIRMED' ||
        deliveryStatus === 'PICKED UP' ||
        deliveryStatus === 'DELIVERED' ||
        deliveryStatus === 'PREPARING') && (
        <Box
          id="alert-container"
          sx={{
            display: 'flex',
            padding: '16px',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '10px',
            alignSelf: 'stretch',
            borderRadius: '12px',
            border: '1px solid var(--Yellow-yellow11, #946800)',
            background: 'var(--Yellow-yellow3, #FFFBD1)',
          }}
        >
          <Typography
            sx={{
              flex: '1 0 0',
              color: 'var(--Gray-gray12, #171717)',
              leadingTrim: 'both',
              textEdge: 'cap',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: 'normal',
            }}
          >
            The{' '}
            <Typography
              component="span"
              sx={{
                color: 'var(--Gray-gray12, #171717)',
                leadingTrim: 'both',
                textEdge: 'cap',
                fontFamily: 'Montserrat',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: 'normal',
              }}
            >
              Address edit window
            </Typography>{' '}
            for this order has closed. You can no longer edit the address.
          </Typography>
        </Box>
      )}
      {deliveryStatus === 'CANCELED' && (
        <Box
          id="alert-container"
          sx={{
            display: 'flex',
            padding: '16px',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '10px',
            alignSelf: 'stretch',
            borderRadius: '12px',
            border: '1px solid var(--Yellow-yellow11, #946800)',
            background: 'var(--Yellow-yellow3, #FFFBD1)',
          }}
        >
          <Typography
            sx={{
              flex: '1 0 0',
              color: 'var(--Gray-gray12, #171717)',
              leadingTrim: 'both',
              textEdge: 'cap',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: 'normal',
            }}
          >
            This order has been{' '}
            <Typography
              component="span"
              sx={{
                color: 'var(--Gray-gray12, #171717)',
                leadingTrim: 'both',
                textEdge: 'cap',
                fontFamily: 'Montserrat',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: 'normal',
              }}
            >
              canceled
            </Typography>{' '}
            . No delivery details to display.
          </Typography>
        </Box>
      )}
      {deliveryStatus === 'DELIVERY FAILED' && (
        <Box
          id="alert-container"
          sx={{
            display: 'flex',
            padding: '12px 0px',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '10px',
            alignSelf: 'stretch',
            borderRadius: '12px',
            border: '1px solid var(--_Tamagui-Red-red11, #CD2B31)',
            background: 'var(--_Tamagui-Red-red4, #FFE5E5)',
          }}
        >
          <ErrorIcon
            sx={{
              width: 'var(--1-5, 24px)',
              height: 'var(--1-5, 24px)',
            }}
          />
          <Typography
            sx={{
              color: 'var(--Gray-gray12, #171717)',
              leadingTrim: 'both',
              textEdge: 'cap',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: 'normal',
            }}
          >
            Delivery failed.
          </Typography>
        </Box>
      )}

      <Drawer
        anchor="right"
        open={openEditDrawer}
        onClose={handleCloseDrawer}
      >
        <AddressEditCard
          deliveryID={delivery.id}
          onClose={handleCloseDrawer}
          // Pass any necessary props to AddressEditCard
        />
      </Drawer>
    </Box>
  );
};

export default DeliveryDetails;
